import { FlexibleSubscription as SliceFlexibleSubscription, ReportSchedule } from '@flexible-reports/store/slices/types';
import { Filters, SubscriptionDetails, TaktSupportedFilters } from '@flexible-reports/hooks/takt-logger/types';
import { buildScheduleType } from '@flexible-reports/store/api/subscriptions/transformers/transformToApiRequest';
import { useGetFlexibleReportConfigurationQuery } from '@flexible-reports/store/api/subscriptions/subscriptions';
import { useQueryReportingFieldsQuery } from '@flexible-reports/store/api/metadata/metadata';
import { FieldType } from '@flexible-reports/store/api/metadata/types';
import {
  buildSelectedFamiliesForDimensions,
  buildSelectedFamiliesForMetrics
} from '@flexible-reports/pages/shared/column-picker-editor/column-picker-editor-utils';
import { logger } from '@flexible-reports/services/logger';
import { Arg, MetricData, RejectedAction } from '@flexible-reports/store/slices/metrics/metrics-types';
import { Filter } from '@urc-ui/store/api/types';
import { FlexibleSubscription as ApiFlexibleSubscription, ScheduleType } from '@flexible-reports/store/api/subscriptions/types';
import { PRESET_PERIOD } from '@flexible-reports/components/date-input/utils/constants';
import { ColumnFamily } from '@flexible-reports/store/metadata.types';

type SubscriptionFields = {
  subscriptionMetrics: string[];
  subscriptionDimensions: string[];
};

type SubscriptionTaktRequirements = {
  flexibleSubscription?: SliceFlexibleSubscription;
  subscriptionMetrics: string[];
  subscriptionDimensions: string[];
};

const useTaktPayloadData = (reportId: string): SubscriptionTaktRequirements => {
  const { data: flexibleSubscription } = useGetFlexibleReportConfigurationQuery({
    reportConfigurationId: reportId
  });

  try {
    const { subscriptionMetrics, subscriptionDimensions } = useTaktReportingFieldsData(flexibleSubscription);
    return { flexibleSubscription: flexibleSubscription || ({} as SliceFlexibleSubscription), subscriptionMetrics, subscriptionDimensions };
  } catch (error) {
    logger.logErrorMessage('Failed to receive report configuration data for takt payload');
    return {
      flexibleSubscription: {} as SliceFlexibleSubscription,
      subscriptionMetrics: [],
      subscriptionDimensions: []
    };
  }
};

const useTaktReportingFieldsData = (flexibleSubscription?: SliceFlexibleSubscription): SubscriptionFields => {
  const { data: reportingFields } = useQueryReportingFieldsQuery({
    typeFilter: { include: [FieldType.DIMENSION_ATTRIBUTE, FieldType.METRIC] }
  });

  if (!reportingFields?.metadata) {
    logger.logErrorMessage('There was an issue fetching the reporting fields metadata for takt payload');
    return { subscriptionMetrics: [], subscriptionDimensions: [] };
  }
  if (!flexibleSubscription) {
    logger.logErrorMessage('Failed to receive report configuration data for takt payload');
    return { subscriptionMetrics: [], subscriptionDimensions: [] };
  }
  try {
    const { dimFamilies = [], metricFamilies = [] } = reportingFields.metadata;
    const subscriptionMetrics = buildSelectedFamiliesForMetrics(metricFamilies, flexibleSubscription?.reportFields || []).flatMap(
      metricFamily => metricFamily.id
    );
    const subscriptionDimensions = buildSelectedFamiliesForDimensions(dimFamilies, flexibleSubscription?.reportFields || []).flatMap(
      dimensionFamily =>
        dimensionFamily.attributes && dimensionFamily.attributes.length > 0 ? dimensionFamily.attributes : dimensionFamily.id
    );

    return { subscriptionMetrics, subscriptionDimensions };
  } catch (error) {
    logger.logErrorMessage('There was an issue fetching the reporting fields metadata for takt payload');
    return { subscriptionMetrics: [], subscriptionDimensions: [] };
  }
};

const buildTaktPayloadfromSliceFlexibleSubscription = (
  flexibleSubscription: SliceFlexibleSubscription,
  selectedMetrics: string[],
  selectedDimensions: string[]
): SubscriptionDetails => {
  if (!flexibleSubscription.reportConfigurationId) {
    return {} as SubscriptionDetails;
  }
  const subscriptionLinkedAccounts = flexibleSubscription.linkedAccounts || [];
  const managerAccountsCount = subscriptionLinkedAccounts.filter(account => account.managerAccountId).length;
  const advertiserAccountsCount = subscriptionLinkedAccounts.filter(account => account.advertiserAccountId).length;

  return {
    subscriptionId: flexibleSubscription.reportConfigurationId,
    latestReportId: flexibleSubscription.latestReportId,
    managerAccountsCount: managerAccountsCount,
    advertiserAccountsCount: advertiserAccountsCount,
    metrics: selectedMetrics || [],
    dimensions: selectedDimensions || [],
    reportPeriod: flexibleSubscription.reportPeriod.preset,
    recipientsCount: flexibleSubscription.reportDelivery.emails.length,
    frequency: buildScheduleType(flexibleSubscription.reportSchedule as ReportSchedule),
    // TODO: Add MTA checkbox state once ready https://sim.amazon.com/issues/RCMRX-10879
    isMtaChecked: false
  };
};

const buildTaktPayloadfromApiFlexibleSubscription = (
  metricFamilies: ColumnFamily[],
  dimFamilies: ColumnFamily[],
  flexibleSubscription?: ApiFlexibleSubscription
): SubscriptionDetails => {
  if (!flexibleSubscription) {
    return {} as SubscriptionDetails;
  }
  return {
    subscriptionId: flexibleSubscription.id,
    latestReportId: flexibleSubscription.reportId,
    managerAccountsCount: flexibleSubscription.accounts.length,
    advertiserAccountsCount: flexibleSubscription.advertisers.length,
    metrics: buildSelectedFamiliesForMetrics(metricFamilies, flexibleSubscription.fields).flatMap(metricFamily => metricFamily.id),
    dimensions: buildSelectedFamiliesForDimensions(dimFamilies, flexibleSubscription.fields).flatMap(dimensionFamily =>
      dimensionFamily.attributes && dimensionFamily.attributes.length > 0 ? dimensionFamily.attributes : [dimensionFamily.id]
    ),
    reportPeriod: flexibleSubscription.reportPeriod.preset ?? PRESET_PERIOD.CUSTOM,
    recipientsCount: flexibleSubscription.recipients.length,
    frequency: flexibleSubscription.frequency as ScheduleType,
    // TODO: Add MTA checkbox state once ready https://sim.amazon.com/issues/RCMRX-10879
    isMtaChecked: false
  };
};

const buildApiSuccessTaktPayload = (endpointName: string, startTime?: number, endTime?: number): MetricData => ({
  endpoint: endpointName,
  success: true,
  status: 200,
  latencyMilliSec: endTime && startTime ? endTime - startTime : undefined,
  timestamp: new Date().toISOString()
});

const buildApiFailureTaktPayload = (endpointName: string, error?: RejectedAction<Arg>): MetricData => ({
  endpoint: endpointName,
  success: false,
  status: error?.error?.status ?? error?.payload?.status ?? 0,
  error: error?.error?.message,
  timestamp: new Date().toISOString()
});

const buildTaktPayloadForFilterUpdates = (filters: Filter[]): Filters | null => {
  const sanitizedFilters = filters.filter(filter => TaktSupportedFilters.includes(filter.column_name));
  if (!sanitizedFilters.length) return null;

  return {
    filters: sanitizedFilters.map(filter => ({
      field: filter.column_name,
      value: filter.values
    }))
  };
};

export {
  useTaktPayloadData,
  useTaktReportingFieldsData,
  buildTaktPayloadfromSliceFlexibleSubscription,
  buildTaktPayloadfromApiFlexibleSubscription,
  buildApiSuccessTaktPayload,
  buildApiFailureTaktPayload,
  buildTaktPayloadForFilterUpdates
};
